import React, { useEffect, useMemo, useRef, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { Button, Link, Stack, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InvPdfDocument from './InvPdfDocument';
import { loadPdfImage } from './pdfImages';

// All invoice outputs share the same image preparation and stamp setting.
// A result belongs to its exact input, so a previous invoice cannot be reused
// while a new PDF or its company images are still being prepared.
export const useInvoicePdf = ({ account, company, values, bankAccounts, logo, stamp,
  invoicePdfLang, checked, freeAppMssg, includeStamp = false }) => {
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState(null);
  const input = useMemo(() => ({ account, company, values, bankAccounts, logo, stamp,
    invoicePdfLang, checked, freeAppMssg, includeStamp, retry }),
  [account, company, values, bankAccounts, logo, stamp, invoicePdfLang, checked, freeAppMssg, includeStamp, retry]);

  useEffect(() => {
    let active = true;
    let url;
    const controller = new AbortController();
    setResult({ input, loading: true, blob: null, url: null, error: null });
    const generate = async () => {
      try {
        const [logoImage, stampImage] = await Promise.all([
          loadPdfImage(logo === undefined ? company?.logo : logo, controller.signal),
          includeStamp ? loadPdfImage(stamp === undefined ? company?.stamp : stamp, controller.signal) : Promise.resolve(null),
        ]);
        if (!active) return;
        const document = InvPdfDocument(account, company, values, bankAccounts, logoImage,
          stampImage, invoicePdfLang, checked, freeAppMssg, includeStamp);
        const blob = await pdf(document).toBlob();
        if (!active) return;
        url = URL.createObjectURL(blob);
        setResult({ input, loading: false, blob, url, error: null });
      } catch (error) {
        if (active) setResult({ input, loading: false, blob: null, url: null, error });
      }
    };
    void generate();
    return () => {
      active = false;
      controller.abort();
      // Allow a just-clicked download to consume its object URL before cleanup.
      if (url) setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
  }, [input, account, company, values, bankAccounts, logo, stamp, invoicePdfLang, checked, freeAppMssg, includeStamp]);

  const current = result?.input === input ? result : { loading: true, blob: null, url: null, error: null };
  return { ...current, retry: () => setRetry((count) => count + 1) };
};

const PdfStatus = ({ instance }) => {
  const { t } = useTranslation();
  if (instance.error) return (
    <Stack role="alert" spacing={1} onClick={(event) => event.stopPropagation()}>
      <Typography color="error">{t('invpdf.generation_failed')}</Typography>
      <Button type="button" onClick={instance.retry}>{t('invpdf.retry')}</Button>
    </Stack>
  );
  return <Typography role="status" variant="button">{t('invpdf.loading_3dots')}</Typography>;
};

const InvPdf = (props) => {
  const { t } = useTranslation();
  const instance = useInvoicePdf(props);
  const downloaded = useRef(null);
  const { setShowPdf, showPdf, values } = props;
  useEffect(() => {
    if (!showPdf || !instance.blob || downloaded.current === instance.blob) return;
    downloaded.current = instance.blob;
    const link = document.createElement('a');
    link.download = `${t('inv.invoice_hash')} ${values.vn || ''}.pdf`;
    link.href = instance.url;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setShowPdf?.(false);
  }, [instance.blob, instance.url, showPdf, setShowPdf, t, values.vn]);
  return instance.loading || instance.error ? <PdfStatus instance={instance} /> : null;
};

export const InvPdfAttachment = (props) => {
  const { t } = useTranslation();
  const instance = useInvoicePdf(props);
  const { setFile, values, invoicePdfLang, setShowPdf } = props;
  useEffect(() => {
    setFile?.(instance.blob || null);
    if (instance.blob) setShowPdf?.(false);
  }, [instance.blob, setFile, setShowPdf]);
  if (instance.loading || instance.error) return <PdfStatus instance={instance} />;
  return (
    <Link href={instance.url} download={`Invoice # ${values?.vn || ''}.pdf`}
      sx={{ textDecoration: 'none', mr: 2, color: '#F40F02', width: '100%' }}>
      <Stack direction="row" sx={{ width: '100%', justifyContent: 'center' }}>
        <PictureAsPdfIcon color="error" />
        <Typography variant="button" sx={{ pl: 1 }}>
          {invoicePdfLang === 'RO/RU' ? t('inv.invoice_hash') : 'Invoice #'} {values?.vn || ''}
        </Typography>
      </Stack>
    </Link>
  );
};

export const InvPdfBlob = (props) => {
  const instance = useInvoicePdf(props);
  const { setFile, setShowPdf } = props;
  useEffect(() => {
    setFile?.(instance.blob || null);
    if (instance.blob) setShowPdf?.(false);
  }, [instance.blob, setFile, setShowPdf]);
  return instance.loading || instance.error ? <PdfStatus instance={instance} /> : null;
};

export default InvPdf;
